import { Injectable } from "@angular/core";
import type {
  IApiResponse,
  Endpoint,
  IHttpUser,
  IHttpUsers,
  IHttpGetTenantTier,
  ProjectUserType,
  PrimaryKeyId,
  GuidId,
} from "src/app/models";
import { IApiResponseBase, ICreateUser, ISetUser } from "src/app/models/http";
import { TenantRole } from "src/app/models/tenant.model";
import { HTTP_METHOD } from ".";
import { HttpService } from "../http.service";
import { UserService } from "../user.service";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class UserApiService {
  #getUser = "GetUser" as Endpoint;
  #getUsers = "GetUsers" as Endpoint;
  #createUser = "CreateUser" as Endpoint;
  #setUser = "SetUser" as Endpoint;
  #getTenantTier = "GetTenantTier" as Endpoint;
  #editTenantRole = "EditTenantRole" as Endpoint;
  #editUserStatus = "EditUserStatus" as Endpoint;

  constructor(
    private readonly http: HttpService,
    private readonly us: UserService
  ) {}

  /**
   * Retrieves a users details
   * @returns {Promise<IApiResponse<IHttpUser>>} User data
   */
  async getUser(): Promise<IApiResponse<IHttpUser>> {
    const { userId } = await this.us.checkAndGetUserIds();
    return firstValueFrom(
      this.http.request$<IHttpUser>(HTTP_METHOD.GET, this.#getUser, {
        params: { userId },
      })
    );
  }

  /**
   * Retrieves a users details
   * @returns {Promise<IApiResponse<IHttpUser>>} User data
   */
  async getUsers(): Promise<IApiResponse<IHttpUsers>> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return firstValueFrom(
      this.http.request$<IHttpUsers>(HTTP_METHOD.GET, this.#getUsers, {
        params: { tenantId },
      })
    );
  }

  /**
   * Creates a new user (Currently used only by Web-portal)
   * @param {ICreateUser} user
   * @returns {Promise<IApiResponse<ApiCreateUser>>} The new TENANT ID and lastmodified
   */
  async createUser(user: ICreateUser): Promise<IApiResponse<ApiCreateUser>> {
    return firstValueFrom(
      this.http.request$<ApiCreateUser>(HTTP_METHOD.POST, this.#createUser, {
        body: user,
      })
    );
  }

  /**
   * Updates an existing users data
   * @param {ISetUser} user User data to update
   * @returns {Promise<IApiResponse>} Success and message
   */
  async setUser(user: ISetUser): Promise<IApiResponse> {
    return firstValueFrom(
      this.http.request$(HTTP_METHOD.PUT, this.#setUser, { body: user })
    );
  }

  /**
   * Get the current users tenant tier data
   * @param props used to pass in ids otherwise reverts to user service values
   * @returns {Promise<IApiResponse<IHttpGetTenantTier>>} Tenant tier and defect count
   */
  async getTenantTier(
    props?: ApiUserTenant
  ): Promise<IApiResponse<IHttpGetTenantTier>> {
    let params: ApiUserTenant = {
      tenantId: undefined,
      userId: undefined,
      tenantGuid: undefined,
      userGuid: undefined,
    };

    if (props) {
      params = props;
    } else {
      const { tenantId, userId } = await this.us.checkAndGetUserIds();
      params.tenantId = tenantId!;
      params.userId = userId!;
    }

    if (
      (params.tenantId === undefined && params.tenantGuid === undefined) ||
      (params.userId === undefined && params.userGuid === undefined)
    ) {
      throw new Error("Invalid request - no ids provided");
    }

    return firstValueFrom(
      this.http.request$<IHttpGetTenantTier>(
        HTTP_METHOD.GET,
        this.#getTenantTier,
        {
          params: params,
        }
      )
    );
  }

  /**
   * Updates a user's role against a tenant
   * @param {PrimaryKeyId} userId
   * @param {string} role
   * @param {string} entityType
   * @returns {Promise<IApiResponseBase>}
   */
  async editTenantRole(
    userId: PrimaryKeyId,
    role: TenantRole,
    entityType: ProjectUserType
  ): Promise<IApiResponseBase> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return firstValueFrom(
      this.http.request$<IApiResponseBase>(
        HTTP_METHOD.PUT,
        this.#editTenantRole,
        {
          body: {
            tenantId,
            userId,
            role,
            entityType,
          },
        }
      )
    );
  }

  /**
   * Updates a users archived status
   * @param {PrimaryKeyId} userId
   * @param {boolean} archived
   * @returns {Promise<IApiResponseBase>}
   */
  async editUserStatus(
    userId: PrimaryKeyId,
    archived: boolean
  ): Promise<IApiResponseBase> {
    const { tenantId } = await this.us.checkAndGetUserIds();
    return firstValueFrom(
      this.http.request$<IApiResponseBase>(
        HTTP_METHOD.PUT,
        this.#editUserStatus,
        {
          body: {
            tenantId,
            userId,
            archived,
          },
        }
      )
    );
  }
}
interface ApiCreateUser {
  readonly id: number;
  readonly lastModified: string;
  readonly userId: number;
}

interface ApiUserTenant {
  tenantId?: number;
  userId?: number;
  tenantGuid?: GuidId;
  userGuid?: GuidId;
}
